import type {
  SchemaBuilderExpressionFormValues,
  SchemaBuilderExpressionVariableFormValue,
  VariablesMap,
} from "./schema-builder-expression.type";
import type {
  SchemaExpressionNode,
  SchemaFieldExpression,
} from "@/entities/schema";
import type { VariableNode } from "@stelar/expression-engine";

import {
  getVariablesNodeFromAST,
  parseStringExpression,
  printExpression,
} from "@stelar/expression-engine";

export const parseExpressionFormula = (
  value: string,
  context?: VariablesMap
) => {
  try {
    const ast = parseStringExpression(value, context);
    const variables = getVariablesNodeFromAST(ast);
    return { ast, variables, error: undefined };
  } catch (error) {
    return { ast: undefined, variables: undefined, error };
  }
};

export const getExpressionValuesFromSchema = (
  schema: SchemaFieldExpression
): SchemaBuilderExpressionFormValues => {
  return {
    formula: printExpression(schema.expression),
    variables: getExpressionVariablesValuesFromSchema(schema),
  };
};

export const getExpressionVariablesValuesFromSchema = (
  schema: SchemaFieldExpression
): SchemaBuilderExpressionVariableFormValue[] => {
  let variables: VariableNode[] = [];

  try {
    variables = getVariablesNodeFromAST(schema.expression);
  } catch {
    /* nothing */
  }

  return variables.map(({ variable, model }) => ({
    variable,
    model: model || undefined, // keep `undefined` to ensure select placeholder is visible
  }));
};

export const getSchemaExpressionNodeFromValues = (
  values: SchemaBuilderExpressionFormValues
): SchemaExpressionNode | undefined => {
  const { formula, variables } = values;

  const context = getVariablesMap(variables);
  const { ast } = parseExpressionFormula(formula, context);

  return ast;
};

export const getVariablesValuesWithExistingValues = (
  variables: VariableNode[],
  existingVariables: SchemaBuilderExpressionVariableFormValue[]
): SchemaBuilderExpressionVariableFormValue[] => {
  const existingValues = getVariablesMap(existingVariables);

  return variables.map(
    ({ variable }): SchemaBuilderExpressionVariableFormValue => ({
      variable,
      model: existingValues[variable] || undefined, // keep `undefined` to ensure select placeholder is visible
    })
  );
};

export const getVariablesMap = (
  variables: SchemaBuilderExpressionVariableFormValue[]
): VariablesMap => {
  return variables.reduce(
    (map, variable) => ({ ...map, [variable.variable]: variable.model ?? "" }),
    {} as VariablesMap
  );
};
